"use client";

import { lazy, Suspense } from "react";
import type { LucideIcon } from "lucide-react";
import { FALLBACK_GLYPH } from "./app-glyphs";

/**
 * Das Glyph einer App, die NICHT in `APP_GLYPHS` steht — aufgeloest ueber einen
 * Namen aus der `infra_apps`-Registry, also erst zur Laufzeit bekannt.
 *
 * WARUM DAS EINE EIGENE DATEI IST UND NICHT VIER ZEILEN IN `AppIcon`:
 * `import { icons } from "lucide-react"` zieht die **komplette** Registry ins
 * Bundle — 1184 Icon-Definitionen, gemessen 434 KB roh und damit 28 % des
 * groessten Chunks der Suite. Tree-shaken kann das niemand, weil der Zugriff
 * mit einem DB-String erfolgt. Bezahlt hat das bisher **jede Seite jeder App**,
 * fuer einen Zweig, den fast nichts erreicht: 33 Apps stehen statisch in
 * `APP_GLYPHS` und kommen hier gar nicht an.
 *
 * Ueber `lazy` + `import()` liegt die Registry jetzt in einem eigenen Chunk, den
 * der Browser nur holt, wenn wirklich eine unbekannte App gezeichnet wird. Die
 * Faehigkeit selbst bleibt unangetastet — eine neue App aus der Registry
 * bekommt ihr Icon weiterhin ohne Code-Aenderung (`AppIcon.tsx:10-14`).
 *
 * `lazy()` verlangt eine stabile Komponenten-Identitaet: bei jedem Render eine
 * neue zu erzeugen wuerde den Baum darunter jedes Mal neu mounten. Deshalb das
 * Register — pro Glyph-Name genau eine Komponente, fuer die Lebensdauer des
 * Dokuments.
 */
const register = new Map<string, LucideIcon>();

function glyphFuer(name: string) {
  const vorhanden = register.get(name);
  if (vorhanden) return vorhanden;

  const komponente = lazy(async () => {
    const { icons } = await import("lucide-react");
    // Der Name kommt aus der DB — ein unbekannter Wert darf nie werfen.
    return { default: icons[name as keyof typeof icons] ?? FALLBACK_GLYPH };
  });
  register.set(name, komponente as unknown as LucideIcon);
  return komponente;
}

export function RegistryGlyph({ name, className }: { name: string; className?: string }) {
  const Glyph = glyphFuer(name);
  return (
    // Der Rueckfall ist das echte Fallback-Glyph, nicht `null`: waehrend der
    // Chunk laedt — und im SSR, wo `lazy` immer den Fallback zeichnet — steht
    // damit ein Zeichen derselben Groesse an derselben Stelle. Sonst haette die
    // Kachel ein Loch und das Icon spraenge hinein.
    <Suspense fallback={<FALLBACK_GLYPH aria-hidden="true" strokeWidth={1.75} className={className} />}>
      <Glyph aria-hidden="true" strokeWidth={1.75} className={className} />
    </Suspense>
  );
}
